import { MAX_CAMERA_ZOOM, MIN_CAMERA_ZOOM } from '@sorrem/shared';
import { useState } from 'react';
import { Slider } from '@sorrem-client/ui/components';
import styles from './SettingsPopover.module.scss';

interface CameraZoomRowProps {
    readonly id: string;
    /** The zoom shown now, 1 being the game's own scale. */
    readonly level: number;
    /** Shown in a run at once while the slider moves, without saving. */
    readonly onPreview: (level: number) => void;
    /** Saves the level, once the slider is let go. */
    readonly onCommit: (level: number) => void;
}

/**
 * The run camera's zoom, as a percent of the game's own scale. Dragging
 * shows it in a run at once; letting go (or a key press settling) saves it,
 * as `VolumeRow` does.
 */
export function CameraZoomRow({ id, level, onPreview, onCommit }: CameraZoomRowProps): JSX.Element {
    const [draft, setDraft] = useState<number | null>(null);
    const percent = Math.round((draft ?? level) * 100);
    const commit = (): void => {
        if (draft !== null) {
            onCommit(draft);
            setDraft(null);
        }
    };

    return (
        <div className={styles.volumeRow}>
            <label className={styles.volumeLabel} htmlFor={id}>Zoom</label>
            <Slider
                id={id}
                min={Math.round(MIN_CAMERA_ZOOM * 100)}
                max={Math.round(MAX_CAMERA_ZOOM * 100)}
                step={5}
                value={percent}
                aria-valuetext={`${percent}%`}
                data-ui-sound="none"
                onChange={(event) => {
                    const next = Number(event.target.value) / 100;
                    setDraft(next);
                    onPreview(next);
                }}
                onPointerUp={commit}
                onKeyUp={commit}
                onBlur={commit}
            />
            <span className={styles.volumeValue}>{percent}%</span>
        </div>
    );
}
